import type { CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import { useIsMobile } from "../../lib/useMediaQuery";

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "checked"> {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: ReactNode;
}

/**
 * Touch-friendly checkbox. The box is scaled to ~20px; when a label is given,
 * the entire <label> is the hit area (min-height var(--tap-min) on mobile) so a
 * finger can land anywhere on the row.
 */
export function Checkbox({
  checked,
  onChange,
  label,
  style,
  ...rest
}: CheckboxProps) {
  const isMobile = useIsMobile();
  const boxStyle: CSSProperties = {
    width: 20,
    height: 20,
    flex: "0 0 auto",
    accentColor: "var(--accent)",
    cursor: "pointer",
  };
  const input = (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={label ? boxStyle : { ...boxStyle, ...style }}
      {...rest}
    />
  );
  if (!label) return input;
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-2)",
        cursor: "pointer",
        minHeight: isMobile ? "var(--tap-min)" : undefined,
        fontSize: 12,
        color: "var(--text-secondary)",
        ...style,
      }}
    >
      {input}
      <span>{label}</span>
    </label>
  );
}
